import { cssVar } from '@chakra-ui/react';
import type { Dict } from '@chakra-ui/utils';
import { getFontFamilyPair, isCursiveFont } from 'ts/common/fonts';

export type IThemeParams = Pick<
    SpApi.IBrandTheme,
    'colorScheme' | 'fontSet' | 'primaryColor' | 'secondaryColor'
>;

export function convertHexToRgba(hex: string, alpha = 1): string {
    const [, r1, r2, g1, g2, b1, b2] = Array.from(hex);
    const blue = parseInt(`${b1}${b2}`, 16);
    const green = parseInt(`${g1}${g2}`, 16);
    const red = parseInt(`${r1}${r2}`, 16);

    if (alpha > 1) {
        alpha = 1;
    } else if (alpha < 0) {
        alpha = 0;
    }

    return `rgba(${red},${green},${blue},${alpha})`;
}

const openSansFont = 'open-sans, sans-serif';

const getTheme = ({ colorScheme, fontSet, primaryColor, secondaryColor }: IThemeParams): Dict => {
    const { primaryFontFamily, secondaryFontFamily } = getFontFamilyPair(fontSet);
    const isNotCursiveFont = !isCursiveFont(fontSet);
    const headingFontFamily = isNotCursiveFont ? primaryFontFamily : secondaryFontFamily;
    const headingFontWeight = fontSet === 'brandon' ? 'bold' : 'normal';
    const headingLetterSpacing = isNotCursiveFont ? 'wider' : 'initial';
    const headingTextTransform = isNotCursiveFont ? 'uppercase' : 'initial';
    const isBambusaFont = fontSet === 'bambusa-pro-regular';
    const isBloomingElegantFont = fontSet === 'blooming-elegant';
    const isLightColorScheme = colorScheme === 'light';
    const dropShadow = `drop-shadow(0px 0px 12px rgba(0,0,0,${
        isLightColorScheme ? '0.2' : '0.8'
    }))`;

    const colors = {
        white: '#FFFFFF',
        whiteAlpha: {
            500: 'rgba(255, 255, 255, 0.4)',
            700: 'rgba(255, 255, 255, 0.8)'
        },
        blackAlpha: {
            500: 'rgba(0, 0, 0, 0.4)',
            700: 'rgba(0, 0, 0, 0.8)'
        },
        gray: {
            25: isLightColorScheme ? '#F7F9FA' : '#1A1A1A',
            50: isLightColorScheme ? 'rgba(100,100,100,0.1)' : 'rgba(155,155,155,0.2)',
            100: isLightColorScheme ? 'rgba(0,0,0,0.1)' : 'rgba(255,255,255,0.2)',
            200: '#E0E3E5',
            300: '#ABB0B2',
            400: '#6E7173',
            500: '#494B4C',
            600: isLightColorScheme ? '#303233' : '#ABB0B2',
            700: isLightColorScheme ? '#191717' : '#121212'
        },
        red: {
            200: '#FFDEDB',
            300: '#FFB9B2',
            400: '#FF7D73',
            600: '#8C1207',
            700: '#520700'
        },
        neutral: {
            100: '#F6F8F8',
            200: '#EDF1F3',
            300: '#BCC8CD',
            400: '#8298A1',
            500: '#607780',
            600: '#344146',
            700: '#141617'
        },
        background: isLightColorScheme
            ? 'var(--chakra-colors-white)'
            : 'var(--chakra-colors-gray-700)',
        paper: isLightColorScheme ? 'var(--chakra-colors-white)' : 'var(--chakra-colors-gray-25)',
        paperBackground: isLightColorScheme
            ? 'var(--chakra-colors-gray-25)'
            : 'var(--chakra-colors-gray-700)',

        boxShadow: isLightColorScheme ? 'rgba(0, 0, 0, 0.05)' : 'rgba(255, 255, 255, 0.04)',
        modalBackground: isLightColorScheme ? 'white' : 'var(--chakra-colors-gray-600)',
        text: isLightColorScheme ? 'var(--chakra-colors-gray-500)' : 'var(--chakra-colors-white)',
        darkText: isLightColorScheme
            ? 'var(--chakra-colors-gray-700)'
            : 'var(--chakra-colors-white)',
        brand: {
            primary: `#${primaryColor}`,
            primaryOverlay: `#${primaryColor}99`,
            primaryMedium: `#${primaryColor}20`,
            primaryLight: `#${primaryColor}10`,
            secondary: `#${secondaryColor}`
        }
    };

    const theme: Dict = {
        colors,
        components: {
            Alert: {
                baseStyle: {
                    container: {
                        boxSizing: 'border-box',
                        fontFamily: openSansFont
                    },
                    title: {
                        fontWeight: 'normal'
                    }
                }
            },
            Badge: {
                baseStyle: {
                    borderRadius: 'full',
                    color: 'text',
                    fontFamily: openSansFont,
                    fontSize: '12px',
                    fontWeight: 600,
                    padding: '3px 12px',
                    textTransform: 'uppercase'
                }
            },
            Button: {
                baseStyle: {
                    borderRadius: 'full',
                    borderStyle: 'solid',
                    borderWidth: '1px',
                    cursor: 'pointer',
                    fontFamily: openSansFont,
                    fontWeight: 'normal',
                    _focus: {
                        boxShadow: 'none'
                    },
                    _disabled: {
                        pointerEvents: 'none'
                    }
                },
                sizes: {
                    md: {
                        fontSize: 'md',
                        height: '48px',
                        letterSpacing: '0.5px',
                        lineHeight: 'base',
                        px: '20px',
                        py: '12px'
                    }
                },
                variants: {
                    primary: {
                        backgroundColor: 'brand.primary',
                        borderColor: 'brand.primary',
                        color: isLightColorScheme ? 'white' : colors.gray[700],
                        _active: {
                            backgroundColor: 'brand.primary'
                        },
                        _after: {
                            backgroundColor: 'transparent',
                            borderRadius: 'full',
                            content: '""',
                            height: 'full',
                            position: 'absolute',
                            transitionDuration: 'normal',
                            transitionProperty: 'common',
                            width: 'full'
                        },
                        _disabled: {
                            opacity: 0.5
                        },
                        _focus: {
                            color: isLightColorScheme ? 'white' : colors.gray[700]
                        },
                        _focusVisible: {
                            boxShadow: `var(--chakra-shadows-outline)`
                        },
                        _hover: {
                            backgroundColor: 'brand.primary',
                            color: isLightColorScheme ? 'white' : colors.gray[700],
                            position: 'relative',
                            _after: {
                                backgroundColor: colors.gray[100]
                            },
                            _disabled: {
                                background: '' // set by default to 'initial' which is not desired
                            }
                        }
                    },
                    'secondary dark': {
                        backgroundColor: 'transparent',
                        borderColor: colors.gray[700],
                        color: colors.gray[700],
                        _active: {
                            backgroundColor: colors.gray[700]
                        },
                        _disabled: {
                            opacity: 0.5
                        },
                        _focus: {
                            color: 'white',
                            backgroundColor: colors.gray[700],
                            borderColor: colors.gray[700]
                        },
                        _focusVisible: {
                            boxShadow: `var(--chakra-shadows-outline)`
                        },
                        _hover: {
                            backgroundColor: colors.gray[700],
                            borderColor: colors.gray[700],
                            color: 'white'
                        }
                    },
                    'secondary light': {
                        backgroundColor: 'transparent',
                        borderColor: 'white',
                        color: 'white',
                        _active: {
                            backgroundColor: 'white'
                        },
                        _disabled: {
                            opacity: 0.5
                        },
                        _focus: {
                            color: colors.gray[700],
                            backgroundColor: 'white',
                            borderColor: 'white'
                        },
                        _focusVisible: {
                            boxShadow: `var(--chakra-shadows-outline)`
                        },
                        _hover: {
                            backgroundColor: 'white',
                            borderColor: 'white',
                            color: colors.gray[700]
                        }
                    },
                    ghost: {
                        backgroundColor: 'transparent',
                        border: 'none',
                        color: 'text',
                        fontSize: 'sm',
                        lineHeight: 'base',
                        paddingX: '16px',
                        _disabled: {
                            opacity: 0.5
                        },
                        _active: {
                            backgroundColor: 'transparent'
                        },
                        _focus: {
                            backgroundColor: 'transparent',
                            color: 'text'
                        },
                        _focusVisible: {
                            boxShadow: 'outline',
                            color: 'brand.primary'
                        },
                        _hover: {
                            backgroundColor: 'transparent',
                            color: 'brand.primary'
                        }
                    },
                    'ghost primary': {
                        backgroundColor: 'transparent',
                        border: 'none',
                        color: 'brand.primary',
                        fontSize: 'sm',
                        lineHeight: 'base',
                        paddingX: '16px',
                        _disabled: {
                            opacity: 0.5
                        },
                        _active: {
                            backgroundColor: 'transparent'
                        },
                        _focus: {
                            backgroundColor: 'transparent',
                            color: 'brand.primary'
                        },
                        _focusVisible: {
                            boxShadow: 'outline',
                            color: 'brand.primary'
                        },
                        _hover: {
                            backgroundColor: 'transparent',
                            color: 'brand.primary'
                        }
                    },
                    googlePhotos: {
                        color: colors.gray[700],
                        border: isLightColorScheme ? '1px solid gray.700' : 'none',
                        backgroundColor: 'white',
                        _focus: {
                            color: colors.gray[700]
                        },
                        _focusVisible: {
                            boxShadow: `var(--chakra-shadows-outline)`
                        },
                        _hover: {
                            backgroundColor: 'white',
                            color: colors.gray[700]
                        }
                    },
                    'outline primary': {
                        backgroundColor: 'transparent',
                        borderColor: 'brand.primary',
                        color: 'brand.primary',
                        lineHeight: 'base',
                        paddingX: '16px',
                        _disabled: {
                            opacity: 0.5
                        },
                        _active: {
                            backgroundColor: 'transparent'
                        },
                        _focus: {
                            backgroundColor: 'transparent',
                            color: 'brand.primary'
                        },
                        _focusVisible: {
                            boxShadow: 'outline',
                            color: 'brand.primary'
                        },
                        _hover: {
                            backgroundColor: 'brand.primary',
                            color: isLightColorScheme ? 'white' : colors.gray[700],
                            position: 'relative',
                            _after: {
                                backgroundColor: 'gray.100'
                            },
                            _disabled: {
                                background: '' // set by default to 'initial' which is not desired
                            }
                        }
                    },
                    text: {
                        color: 'brand.primary',
                        padding: 0,
                        fontWeight: 'semibold',
                        fontSize: '14px',
                        backgroundColor: 'transparent',
                        border: 'none',
                        cursor: 'pointer',
                        verticalAlign: 'baseline',
                        textDecoration: 'underline',
                        _hover: {
                            backgroundColor: 'transparent',
                            color: 'brand.primary'
                        }
                    }
                }
            },
            Checkbox: {
                baseStyle: {
                    control: {
                        borderColor: 'gray.300',
                        borderWidth: '1px',
                        _checked: {
                            background: 'transparent',
                            borderColor: 'gray.300',
                            _hover: {
                                background: 'transparent',
                                borderColor: 'gray.300'
                            }
                        }
                    },
                    icon: {
                        color: 'brand.primary'
                    },
                    label: {
                        color: 'text',
                        fontFamily: openSansFont,
                        fontWeight: 'normal'
                    }
                },
                sizes: {
                    xl: {
                        control: {
                            boxSize: 26
                        }
                    }
                },
                variants: {
                    round: {
                        control: {
                            borderRadius: '100%',
                            color: isLightColorScheme ? 'white' : colors.gray[700],
                            borderColor: isLightColorScheme ? 'white' : colors.gray[700],
                            background: 'gray.100',
                            ' svg': {
                                width: '12px',
                                height: '12px'
                            },
                            _checked: {
                                color: isLightColorScheme ? 'white' : colors.gray[700],
                                borderColor: isLightColorScheme ? 'white' : colors.gray[700],
                                background: 'brand.primary',
                                _hover: {
                                    borderColor: isLightColorScheme ? 'white' : colors.gray[700],
                                    background: 'brand.primary'
                                }
                            }
                        }
                    }
                }
            },
            DatePicker: {
                baseStyle: {
                    fontFamily: 'open-sans, sans-serif',
                    backgroundColor: 'var(--chakra-colors-brand-primary)',
                    headerBackgroundColor: isLightColorScheme ? 'white' : 'transparent',
                    calendarBackgroundColor: isLightColorScheme ? 'white' : 'transparent',
                    popperBackgroundColor: isLightColorScheme ? 'white' : 'transparent',
                    color: isLightColorScheme ? '#141617' : 'white',
                    dateColor: isLightColorScheme ? 'white' : 'black',
                    disabledColor: isLightColorScheme ? '#cccccc' : 'grey',
                    // Additional styling to preserve original client look
                    dayHeaderColor: isLightColorScheme ? '#141617' : 'white',
                    chevronColor: isLightColorScheme ? '#141617' : 'white',
                    borderColor: 'gray',
                    borderRadius: '4px',
                    boxShadow: 'none',
                    padding: '0',
                    daySize: '42px',
                    dayFontSize: 'inherit',
                    hoverBackgroundColor: 'none'
                }
            },
            Divider: {
                baseStyle: {
                    borderWidth: '1px',
                    borderStyle: 'solid',
                    borderColor: isLightColorScheme ? 'gray.200' : 'gray.500'
                },
                variants: {
                    dark: {
                        borderColor: colors.gray[700]
                    },
                    light: {
                        borderColor: 'white'
                    },
                    primary: {
                        borderColor: 'brand.primary'
                    }
                }
            },
            Drawer: {
                baseStyle: {
                    dialog: {
                        backgroundColor: 'modalBackground',
                        borderRadius: '20px 20px 0 0',
                        boxShadow: 'none',
                        filter: dropShadow
                    },
                    closeButton: {
                        marginTop: '-48px',
                        borderRadius: 'xl',
                        right: '16px',
                        backgroundColor: isLightColorScheme ? 'whiteAlpha.900' : colors.gray[600],
                        color: 'text',
                        _active: {
                            backgroundColor: isLightColorScheme
                                ? 'whiteAlpha.900'
                                : colors.gray[600],
                            color: 'text'
                        },
                        _hover: {
                            color: 'text',
                            boxShadow: 'none'
                        },
                        _focus: {
                            color: 'text',
                            boxShadow: 'none'
                        },
                        _focusVisible: {
                            boxShadow: 'outline'
                        }
                    }
                }
            },
            Form: {
                baseStyle: {
                    container: {
                        fontFamily: openSansFont,
                        paddingBottom: 6
                    },
                    requiredIndicator: {
                        color: 'inherit',
                        margin: 0
                    }
                }
            },
            FormError: {
                baseStyle: {
                    text: {
                        color: isLightColorScheme
                            ? 'var(--chakra-colors-red-600)'
                            : 'var(--chakra-colors-red-400)',
                        fontFamily: openSansFont,
                        lineHeight: 'shorter',
                        marginTop: '2px'
                    }
                }
            },
            FormLabel: {
                baseStyle: {
                    color: 'text',
                    fontFamily: openSansFont,
                    marginBottom: '2px',
                    marginRight: '6px',
                    fontWeight: 'normal'
                },
                sizes: {
                    md: {
                        fontSize: 'md',
                        lineHeight: 'base'
                    }
                },
                defaultProps: {
                    size: 'md'
                }
            },
            Heading: {
                sizes: {
                    '3xl': {
                        fontFamily: headingFontFamily,
                        fontSize: isNotCursiveFont ? '3xl' : isBloomingElegantFont ? '5xl' : '4xl',
                        fontWeight: headingFontWeight,
                        letterSpacing: headingLetterSpacing,
                        lineHeight: isNotCursiveFont || isBloomingElegantFont ? 'shorter' : 'base',
                        textTransform: headingTextTransform
                    },
                    '2xl': {
                        fontFamily: headingFontFamily,
                        fontSize: isNotCursiveFont ? '2xl' : isBloomingElegantFont ? '4xl' : '3xl',
                        fontWeight: headingFontWeight,
                        letterSpacing: headingLetterSpacing,
                        lineHeight: isNotCursiveFont || isBloomingElegantFont ? 'shorter' : 'base',
                        textTransform: headingTextTransform
                    },
                    xl: {
                        fontFamily: headingFontFamily,
                        fontSize: isNotCursiveFont ? 'lg' : isBloomingElegantFont ? '3xl' : '2xl',
                        fontWeight: headingFontWeight,
                        letterSpacing: headingLetterSpacing,
                        lineHeight: isNotCursiveFont || isBloomingElegantFont ? 'shorter' : 'base',
                        textTransform: headingTextTransform
                    },
                    lg: {
                        fontFamily: openSansFont,
                        fontSize: 'lg',
                        fontWeight: 'semibold',
                        lineHeight: 'short'
                    },
                    md: {
                        fontFamily: openSansFont,
                        fontSize: 'md',
                        fontWeight: 'semibold',
                        lineHeight: 'base'
                    },
                    sm: {
                        fontFamily: headingFontFamily,
                        fontSize:
                            isNotCursiveFont || isBambusaFont
                                ? '16px'
                                : isBloomingElegantFont
                                ? 'xl'
                                : 'lg',
                        fontWeight: headingFontWeight,
                        letterSpacing: headingLetterSpacing,
                        lineHeight: isNotCursiveFont || isBloomingElegantFont ? 'short' : 'base',
                        textTransform: headingTextTransform
                    },
                    xs: {
                        fontFamily: openSansFont,
                        fontSize: 'sm',
                        letterSpacing: 'wider',
                        lineHeight: 'short',
                        textTransform: 'uppercase'
                    }
                },
                variants: {
                    dark: {
                        color: colors.gray[700]
                    },
                    light: {
                        color: 'white'
                    },
                    primary: {
                        color: 'brand.primary'
                    }
                }
            },
            Input: {
                baseStyle: {
                    field: {
                        boxSizing: 'border-box',
                        color: 'text',
                        fontFamily: openSansFont,
                        _placeholder: {
                            color: 'neutral.300'
                        }
                    },
                    element: {
                        fontFamily: openSansFont,
                        color: 'gray.400'
                    }
                },
                variants: {
                    outline: {
                        field: {
                            borderColor: 'gray.300',
                            transition: 'outline .1s ease-in',
                            outlineOffset: '-1px',
                            _focus: {
                                outlineColor: 'brand.primary',
                                outlineWidth: '2px',
                                boxShadow: 'none'
                            },
                            _hover: {
                                outlineColor: 'brand.primary',
                                outlineWidth: '2px'
                            },
                            _invalid: {
                                outlineColor: isLightColorScheme ? 'red.600' : 'red.400',
                                outlineWidth: '2px',
                                boxShadow: 'none'
                            }
                        }
                    }
                }
            },
            Link: {
                baseStyle: {
                    color: 'brand.primary',
                    _active: {
                        color: 'brand.primary'
                    },
                    _focus: {
                        color: 'brand.primary'
                    },
                    _hover: {
                        color: 'brand.primary'
                    },
                    _visited: {
                        color: 'brand.primary'
                    }
                },
                variants: {
                    googlePhotos: {
                        _focus: {
                            boxShadow: 'none'
                        },
                        _focusVisible: {
                            boxShadow: `var(--chakra-shadows-outline)`
                        }
                    }
                }
            },
            List: {
                baseStyle: {
                    item: {
                        fontFamily: openSansFont
                    }
                }
            },
            Modal: {
                baseStyle: {
                    body: {
                        bg: 'background',
                        borderRadius: {
                            base: 'none',
                            lg: 'xl'
                        },
                        fontFamily: openSansFont,
                        pb: {
                            base: '24px',
                            lg: '40px'
                        },
                        pt: '40px',
                        px: {
                            base: '26px',
                            lg: '56px'
                        }
                    },
                    closeButton: {
                        color: 'text',
                        borderRadius: 'full',
                        cursor: 'pointer',
                        _active: {
                            backgroundColor: isLightColorScheme
                                ? 'blackAlpha.200'
                                : 'whiteAlpha.200'
                        },
                        _focus: {
                            color: 'text',
                            boxShadow: 'none'
                        },
                        _focusVisible: {
                            boxShadow: 'outline'
                        },
                        _hover: {
                            color: 'text',
                            backgroundColor: isLightColorScheme ? 'gray.100' : 'gray.600'
                        }
                    },
                    dialog: {
                        bg: 'background',
                        borderRadius: {
                            base: 'none',
                            lg: 'xl'
                        },
                        my: {
                            base: 0,
                            lg: '60px'
                        }
                    },
                    header: {
                        fontFamily: openSansFont
                    },
                    overlay: {
                        bg: isLightColorScheme ? 'blackAlpha.600' : 'whiteAlpha.600'
                    }
                },
                sizes: {
                    md: {
                        content: {
                            maxHeight: 'calc(100vh - 80px)'
                        },
                        dialog: {
                            height: {
                                base: 'full',
                                lg: 'auto'
                            },
                            maxWidth: {
                                base: 'full',
                                lg: 'xl'
                            }
                        }
                    }
                }
            },
            Popover: {
                baseStyle: {
                    body: {
                        padding: '20px 40px 40px 40px'
                    },
                    content: {
                        backgroundColor: isLightColorScheme ? 'background' : 'gray.600',
                        border: 'none',
                        borderRadius: 'xl',
                        filter: dropShadow,
                        width: '380px',
                        _focus: {
                            boxShadow: 'none'
                        },
                        _focusVisible: {
                            boxShadow: 'outline'
                        }
                    }
                },
                variants: {
                    small: {
                        body: {
                            padding: '10px 0'
                        },
                        content: {
                            width: 'initial'
                        }
                    }
                }
            },
            Radio: {
                baseStyle: {
                    control: {
                        borderColor: 'gray.300',
                        borderWidth: '1px',
                        _checked: {
                            background: 'transparent',
                            borderColor: 'gray.300',
                            _hover: {
                                background: 'transparent',
                                borderColor: 'gray.300'
                            },
                            // Styles applied to the inner circle when checked:
                            _before: {
                                background: 'brand.primary',
                                width: '100%',
                                height: '100%',
                                transform: 'scale(0.67)'
                            }
                        }
                    },
                    label: {
                        color: 'text',
                        fontFamily: openSansFont
                    }
                }
            },
            Select: {
                baseStyle: {
                    field: {
                        boxSizing: 'border-box',
                        color: 'text',
                        fontFamily: openSansFont
                    }
                },
                variants: {
                    outline: {
                        field: {
                            borderColor: 'gray.300',
                            paddingX: '16px',
                            _focus: {
                                borderColor: 'brand.primary',
                                borderWidth: '2px',
                                boxShadow: 'none',
                                paddingX: '15px'
                            },
                            _hover: {
                                borderColor: 'brand.primary',
                                borderWidth: '2px',
                                paddingX: '15px'
                            },
                            _invalid: {
                                borderColor: isLightColorScheme ? 'red.600' : 'red.400',
                                borderWidth: '2px',
                                boxShadow: 'none',
                                paddingX: '15px'
                            }
                        }
                    }
                }
            },
            Table: {
                baseStyle: {
                    th: {
                        fontFamily: openSansFont,
                        fontWeight: '600',
                        letterSpacing: 'normal',
                        textTransform: 'none'
                    },
                    td: {
                        fontFamily: openSansFont,
                        fontWeight: 'normal',
                        verticalAlign: 'top'
                    }
                },
                sizes: {
                    md: {
                        tr: {
                            position: 'relative',
                            left: '-12px'
                        },
                        td: {
                            paddingInlineEnd: '12px',
                            paddingInlineStart: '12px'
                        },
                        th: {
                            fontSize: '16px',
                            paddingInlineEnd: '12px',
                            paddingInlineStart: '12px'
                        }
                    }
                }
            },
            Text: {
                baseStyle: {
                    color: 'text',
                    lineHeight: 'base',
                    margin: 0,
                    fontFamily: openSansFont
                },
                variants: {
                    error: {
                        color: isLightColorScheme ? 'red.600' : 'red.400'
                    }
                }
            },
            Textarea: {
                baseStyle: {
                    boxSizing: 'border-box',
                    color: 'text',
                    fontFamily: openSansFont,
                    resize: 'vertical',
                    _placeholder: {
                        color: 'neutral.300'
                    }
                },
                variants: {
                    outline: {
                        borderColor: 'gray.300',
                        transition: 'outline .1s ease-in',
                        outlineOffset: '-1px',
                        _focus: {
                            outlineColor: 'brand.primary',
                            outlineWidth: '2px',
                            boxShadow: 'none'
                        },
                        _hover: {
                            outlineColor: 'brand.primary',
                            outlineWidth: '2px'
                        },
                        _invalid: {
                            outlineColor: isLightColorScheme ? 'red.600' : 'red.400',
                            outlineWidth: '2px',
                            boxShadow: 'none'
                        }
                    }
                }
            },
            Tooltip: {
                baseStyle: {
                    background: isLightColorScheme ? 'background' : 'gray.600',
                    // This workaround is necessary, because Chakra has a bug and won't set the bg of the tooltip's arrow:
                    // See https://github.com/chakra-ui/chakra-ui/issues/4695#issuecomment-991023319
                    [cssVar('popper-arrow-bg').variable]: isLightColorScheme
                        ? 'var(--chakra-colors-white)'
                        : 'var(--chakra-colors-gray-600)',
                    borderRadius: 'lg',
                    boxShadow: 'none',
                    color: 'text',
                    filter: dropShadow,
                    fontFamily: openSansFont,
                    fontSize: '14px',
                    lineHeight: 'tall',
                    padding: '16px',
                    zIndex: 10000
                }
            }
        },
        filters: {
            dropShadow
        },
        fontColor: {
            default: 'text'
        },
        // Avoid applying fonts directly to the body and other types to avoid conflicts with other styles
        fonts: {
            body: '',
            heading: '',
            mono: ''
        },
        fontSizes: {
            xs: '12px',
            sm: '14px',
            md: '16px',
            lg: '20px',
            xl: '24px',
            '2xl': '28px',
            '3xl': '40px',
            '4xl': '44px',
            '5xl': '52px'
        },
        isLightColorScheme,
        letterSpacings: {
            wide: '1px',
            wider: '2px'
        },
        radii: {
            md: '3px',
            lg: '4px',
            xl: '20px'
        },
        shadows: {
            md: `0 0 4px 2px ${'var(--chakra-colors-gray-100)'}`
        },
        sizes: {
            xs: '320px',
            sm: '400px',
            xl: '600px'
        },
        utility: {
            convertHexToRgba
        }
    };

    return theme;
};

export default getTheme;
